/* eslint-disable @typescript-eslint/no-floating-promises */
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { InstanceService } from '@commonspirit/ngx-instance';
import { forkJoin, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { KeywordSearchSuggestions, LocationSuggestionsResponse, SessionConstants } from '../../models/v2/search';
import { ResizeService, ViewPortSize } from '../../services/resize/resize.service';
import { SearchService } from '../../services/search/search.service';
import '../../../core/util/string-prototypes';
import { ConfigurationKey } from 'src/app/core/models/configuration-key';
import { EnvironmentService } from '@commonspirit/ngx-environment';
import { EnvValue } from '@commonspirit/ngx-environment/lib/models/key-value-pair';

@Component({
  selector: 'fal-keyword-search-box',
  templateUrl: './keyword-search-box.component.html',
  styleUrls: ['./keyword-search-box.component.scss']
})
export class KeywordSearchBoxComponent implements OnInit, OnDestroy {
  @Input() searchTerm: string;
  @Output() searchTermChanged = new EventEmitter<string>();
  @Output() searchTermSelected = new EventEmitter<{ id: string; name: string; isService: boolean }>();
  @Output() keywordSelected = new EventEmitter<string>();
  @Output() keywordEnter = new EventEmitter<string>();
  @Output() goToLocationEvent = new EventEmitter<any>();
  @Input() showSearchBtn?: boolean;
  @Input() hideKeywordSuggestions: boolean;
  @Output() tabPressedOnKeywordSearch = new EventEmitter<any>();
  @Output() closeLocationSuggestion = new EventEmitter<any>();

  suggestions: KeywordSearchSuggestions = {
    locations: [],
    services: [],
    keywords: []
  };
  autocompleteActive: boolean;
  defaultSuggestionCount = 3;
  locationSliceIndex = this.defaultSuggestionCount;
  serviceSliceIndex = this.defaultSuggestionCount;
  keywordSliceIndex = this.defaultSuggestionCount;
  searchPlaceholder = 'Services, location name or keyword';
  showLocationSuggestions: boolean;
  activeLocationDropdownIndex = -1;
  activeServiceDropdownIndex = -1;
  activeKeywordDropdownIndex = -1;
  defaultSliceIndex = 2;
  lastFocusWasFromKeyboard = false;
  seeLessMoreClicked = false;
  seeLessMoreFocused = false;
  isFromExpandLessClick = false;
  autoSuggestionSub: Subscription;
  viewPort$: Subscription;
  results$: Subscription;
  subject = new ReplaySubject<string>(1);
  private readonly inputDebounceMS = 300;
  private readonly noSuggestionsSelected = -1;
  private inputModelChanged: Subject<string> = new Subject<string>();
  loadSpinnerSuggestions = false;
  inputInFocus = false;
  searchSelected = false;
  enableBlockIT: boolean;

  @ViewChild('locationSuggestionsContainer') locationSuggestionsContainer: ElementRef;
  @ViewChild('search') searchElement: ElementRef;
  @ViewChild('locationSeeMore') locationSeeMore: ElementRef;
  @ViewChild('servicesSeeMore') servicesSeeMore: ElementRef;
  @ViewChild('keywordsSeeMore') keywordsSeeMore: ElementRef;
  loadSuggestions: string;

  @HostListener('window:click', ['$event'])
  onClick(event: MouseEvent): void {
    if (
      !(this.locationSuggestionsContainer.nativeElement as HTMLElement)?.contains(event.target as HTMLElement) &&
      (event?.target as HTMLElement)?.dataset?.type !== 'viewMore'
    ) {
      this.setAutocompleteActive(false);
      this.locationSliceIndex = this.defaultSuggestionCount;
      this.serviceSliceIndex = this.defaultSuggestionCount;
      this.keywordSliceIndex = this.defaultSuggestionCount;
      this.activeLocationDropdownIndex = this.noSuggestionsSelected;
      this.activeServiceDropdownIndex = this.noSuggestionsSelected;
      this.activeKeywordDropdownIndex = this.noSuggestionsSelected;
    } else if (
      this.searchTerm &&
      this.searchTerm !== ' ' &&
      (this.suggestions.locations.length > 0 || this.suggestions.services.length > 0 || this.suggestions.keywords.length > 0)
    ) {
      this.autocompleteActive = true;
    }
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(): void {
    this.lastFocusWasFromKeyboard = false;
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(keyEvent: KeyboardEvent): void {
    const keyCode = keyEvent.key.toLowerCase();
    if (keyCode === 'arrowdown' || keyCode === 'arrowup') {
      if (this.seeLessMoreClicked) {
        if (this.seeLessMoreFocused) {
          this.seeLessMoreFocused = false;
          (this.searchElement.nativeElement as HTMLElement).focus();
          this.keyDownHandler(keyEvent);
        } else {
          this.seeLessMoreFocused = true;
        }
      }
    }
    if (keyCode === 'tab' && this.autocompleteActive) {
      this.tabPressedOnKeywordSearch.emit();
    }
  }

  constructor(
    private resizeService: ResizeService,
    public instanceService: InstanceService,
    private searchService: SearchService,
    private router: Router,
    public environmentService: EnvironmentService
  ) {
    const enableBlockITForEnvironment: EnvValue | undefined = this.environmentService.getSetting('enableBlockIT')?.value;
    if (typeof enableBlockITForEnvironment === 'boolean') {
      this.enableBlockIT = enableBlockITForEnvironment;
    }
  }

  ngOnInit(): void {
    this.loadSuggestions = this.instanceService.getConfiguration(ConfigurationKey.LoadingSuggestion)?.value as string;
    this.viewPort$ = this.resizeService.viewPortChanged.pipe(tap((result: ViewPortSize) => this.setPlaceholderText(result))).subscribe();

    this.results$ = this.subject
      .pipe(
        filter((searchText) => !!searchText && searchText.length > 0),
        switchMap((searchTerm) => this.getSearchSuggestions(searchTerm)),
        tap(() =>
          this.setAutocompleteActive(
            this.suggestions.locations.length > 0 || this.suggestions.keywords.length > 0 || this.suggestions.services.length > 0
          )
        )
      )
      .subscribe();

    this.inputModelChanged.pipe(debounceTime(this.inputDebounceMS)).subscribe((event) => this.onChangeSearch(event));
  }

  ngOnDestroy(): void {
    this.results$.unsubscribe();
    this.subject.unsubscribe();
    this.viewPort$.unsubscribe();
  }

  setPlaceholderText(viewportSize: ViewPortSize): void {
    // TODO: set searchPlaceholder text based on viewport size and configuration
  }

  getSearchSuggestions(query: string): Observable<LocationSuggestionsResponse> {
    return this.getLocationSuggestionsTyped(query);
  }

  getLocationSuggestionsTyped(query: string): Observable<LocationSuggestionsResponse> {
    // TODO: call the suggestions API
    return {} as Observable<LocationSuggestionsResponse>;
  }

  onInputChange(val: string): void {
    this.inputModelChanged.next(val);
    this.searchSelected = false;
    if (!val) window.sessionStorage.removeItem(SessionConstants.SelectedSuggestion);
  }

  onChangeSearch(val: string): void {
    this.serviceSliceIndex = 3;
    this.keywordSliceIndex = 3;
    this.locationSliceIndex = 3;
    this.searchTerm = val.trim();
    this.searchTermChanged.emit(val);
    this.autocompleteActive = false;
    if (this.searchTerm.length >= 2) {
      if (this.enableBlockIT) {
        this.autoSuggestionSub = forkJoin(
          this.searchService.getAutoSuggestionLocations(this.searchTerm),
          this.searchService.getAutoSuggestionServicesForBlockIt(this.searchTerm)
        ).subscribe((result) => {
          result[1]?.items.forEach((x) => (x.isService = true));
          this.suggestions.locations = result[0].items;
          this.suggestions.services = result[1].items;
          this.setAutocompleteActive(true);
          this.loadSpinnerSuggestions = false;
        });
      } else {
        this.autoSuggestionSub = forkJoin(
          this.searchService.getAutoSuggestionLocations(this.searchTerm),
          this.searchService.getAutoSuggestionServices(this.searchTerm),
          this.searchService.getAutoSuggestionKeywords(this.searchTerm)
        ).subscribe((result) => {
          this.suggestions.locations = result[0].items;
          this.suggestions.services = result[1].results.map((value) => ({
            isService: true,
            id: value
              ?.trim()
              .replace(/[^A-Z0-9]+/gi, ' ')
              .trimEnd(),
            name: value
          }));
          this.suggestions.keywords = result[2].results.map((value) => ({
            isService: false,
            id: value
              ?.trim()
              .replace(/[^A-Z0-9]+/gi, ' ')
              .trimEnd(),
            name: value
          }));
          this.setAutocompleteActive(true);
          this.loadSpinnerSuggestions = false;
        });
      }
    } else {
      this.setAutocompleteActive(false);
    }

    this.locationSliceIndex = this.defaultSuggestionCount;
    this.serviceSliceIndex = this.defaultSuggestionCount;
    this.keywordSliceIndex = this.defaultSuggestionCount;
    this.suggestions = {
      locations: [],
      services: [],
      keywords: []
    };

    val = val.replace(/^[^a-zA-Z0-9&-]+$/, '');
    if (val && /^ *$/.exec(val) === null) {
      this.subject.next(val);
      this.searchTermChanged.emit(val);
    } else {
      this.setAutocompleteActive(false);
    }
  }

  setAutocompleteActive(value: boolean): void {
    if (value === true) {
      if (this.suggestions.locations.length > 0) {
        this.activeLocationDropdownIndex = 0;
        this.activeServiceDropdownIndex = this.noSuggestionsSelected;
        this.activeKeywordDropdownIndex = this.noSuggestionsSelected;
      } else if (this.suggestions.services.length > 0) {
        this.activeServiceDropdownIndex = 0;
        this.activeLocationDropdownIndex = this.noSuggestionsSelected;
        this.activeKeywordDropdownIndex = this.noSuggestionsSelected;
      } else if (this.suggestions.keywords.length > 0) {
        this.activeKeywordDropdownIndex = 0;
        this.activeLocationDropdownIndex = this.noSuggestionsSelected;
        this.activeServiceDropdownIndex = this.noSuggestionsSelected;
      }
    }
    this.autocompleteActive = value;
  }

  locationClick(loc: { id: string; name: string }): void {
    this.searchTerm = loc.name;
    this.autocompleteActive = false;
    // set return url for location detail back button using the current route
    sessionStorage.setItem('searchUrl', this.router.url);
    let locationNavLink;
    if (this.enableBlockIT) {
      locationNavLink = `/${loc.id}`;
    } else {
      locationNavLink = `/${loc.name
        .replace(/[^A-Z0-9]+/gi, ' ')
        .toUrlFriendly()
        .stripPeriod()
        .condenseDashes()
        .removeSpecialCharacters()
        .sanitizeHyphens()}-${loc.id}`;
    }

    if (location.hostname?.includes('vmfh') && !(location.pathname?.length > 1)) {
      const falPageUrl = this.instanceService.getConfiguration(ConfigurationKey.FalPage)?.value as { link: string };
      window.location.href = `${falPageUrl.link}` + locationNavLink;
    } else if (this.router.url === '/fal-lite') window.location.href = `${location.pathname.replace('/fal-lite', '')}` + locationNavLink;
    else this.router.navigate([locationNavLink]);
  }

  dropdownClick(selected: { id: string; name: string; isService: boolean }): void {
    this.autocompleteActive = this.loadSpinnerSuggestions = false;
    this.searchTerm = selected.name;
    this.searchTermSelected.emit(selected);
    this.searchSelected = true;
    window.sessionStorage.removeItem(SessionConstants.SelectedSuggestion);
  }

  goToLocation(name: string, id: string): void {
    sessionStorage.setItem('searchResultsOrigin', 'true');
    this.goToLocationEvent.emit({ name, id });
  }

  queryHighlight(location: string): string {
    const reg = new RegExp(this.searchTerm, 'gi');
    return location.replace(reg, (match: string) => `<em class='highlight'>${match}</em>`);
  }

  handleKeywordChange(): void {
    if (this.seeLessMoreClicked) {
      this.seeLessMoreClicked = false;
      return;
    }

    this.seeLessMoreClicked = false;

    let locationChosen = false;
    let serviceChosen = false;
    let keywordChosen = false;
    document.querySelectorAll('.locations-list li').forEach((item) => {
      if (!locationChosen) {
        locationChosen = item.classList.contains('selected');
      }
    });
    document.querySelectorAll('.service-list li').forEach((item) => {
      if (!serviceChosen) {
        serviceChosen = item.classList.contains('selected');
      }
    });
    document.querySelectorAll('.keyword-list li').forEach((item) => {
      if (!keywordChosen) {
        keywordChosen = item.classList.contains('selected');
      }
    });

    if (locationChosen && this.autocompleteActive) {
      const selectedLocation = this.suggestions.locations[this.activeLocationDropdownIndex];
      this.goToLocation(selectedLocation.name, selectedLocation.id);
      this.locationClick({ id: selectedLocation.id, name: selectedLocation.name });
    } else if (serviceChosen && this.autocompleteActive) {
      const selectedService = this.suggestions.services[this.activeServiceDropdownIndex];
      this.dropdownClick({ id: selectedService.id, name: selectedService.name, isService: selectedService.isService });
    } else if (keywordChosen && this.autocompleteActive) {
      const selectedKeyword = this.suggestions.keywords[this.activeKeywordDropdownIndex];
      this.dropdownClick({ id: selectedKeyword.id, name: selectedKeyword.name, isService: false });
    } else {
      this.keywordEnter.emit(this.searchTerm);
    }
    this.autocompleteActive = false;
  }

  keyDownHandler(keyEvent: KeyboardEvent): void {
    const keyCode = keyEvent.key.toLowerCase();
    if (keyCode === 'arrowdown') {
      keyEvent.preventDefault();
      this.arrowHandler(1);
    } else if (keyCode === 'arrowup') {
      keyEvent.preventDefault();
      this.arrowHandler(-1);
    } else if (keyCode === 'delete') {
      this.loadSpinnerSuggestions = false;
    } else if (keyCode != 'tab') {
      if (this.searchTerm.trim().length > 1) {
        this.loadSpinnerSuggestions = true;
      } else {
        this.loadSpinnerSuggestions = false;
      }
    }
  }

  handleMouseEnter(index: number, type: string): void {
    if (!this.lastFocusWasFromKeyboard) {
      if (this.seeLessMoreClicked) {
        this.seeLessMoreClicked = false;
        this.seeLessMoreFocused = false;
        (this.searchElement.nativeElement as HTMLElement).focus();
      }
      if (type == 'location') {
        this.activeServiceDropdownIndex = this.noSuggestionsSelected;
        this.activeKeywordDropdownIndex = this.noSuggestionsSelected;
        this.activeLocationDropdownIndex = index;
      } else if (type == 'services') {
        this.activeLocationDropdownIndex = this.noSuggestionsSelected;
        this.activeKeywordDropdownIndex = this.noSuggestionsSelected;
        this.activeServiceDropdownIndex = index;
      } else if (type == 'keywords') {
        this.activeLocationDropdownIndex = this.noSuggestionsSelected;
        this.activeServiceDropdownIndex = this.noSuggestionsSelected;
        this.activeKeywordDropdownIndex = index;
      }
    }
  }

  arrowHandler(increment: number): void {
    this.lastFocusWasFromKeyboard = true;
    this.seeLessMoreClicked = false;

    if (this.activeLocationDropdownIndex > this.noSuggestionsSelected) {
      if (this.activeLocationDropdownIndex + increment === -1) {
        this.activeLocationDropdownIndex = 0;
        this.activeServiceDropdownIndex = this.noSuggestionsSelected;
        this.activeKeywordDropdownIndex = this.noSuggestionsSelected;
      } else if (this.activeLocationDropdownIndex + increment < this.locationSliceIndex) {
        this.activeLocationDropdownIndex += increment;
        this.activeServiceDropdownIndex = this.noSuggestionsSelected;
        this.activeKeywordDropdownIndex = this.noSuggestionsSelected;
      } else if (this.activeLocationDropdownIndex + increment === this.locationSliceIndex && this.locationSeeMore) {
        this.activeLocationDropdownIndex += increment;
        this.seeLessMoreClicked = true;
        this.locationSeeMore.nativeElement.focus();
      } else {
        this.activeLocationDropdownIndex = this.noSuggestionsSelected;
        this.activeServiceDropdownIndex = 0;
        this.activeKeywordDropdownIndex = this.noSuggestionsSelected;
      }
    } else if (this.activeServiceDropdownIndex > this.noSuggestionsSelected) {
      if (this.activeServiceDropdownIndex + increment === -1) {
        if (this.locationSeeMore) {
          this.seeLessMoreClicked = true;
          this.locationSeeMore.nativeElement.focus();
          this.activeLocationDropdownIndex = this.locationSliceIndex;
        } else {
          this.activeLocationDropdownIndex = this.locationSliceIndex - 1;
        }
        this.activeKeywordDropdownIndex = this.noSuggestionsSelected;
        this.activeServiceDropdownIndex = this.noSuggestionsSelected;
      } else if (this.activeServiceDropdownIndex + increment < this.serviceSliceIndex) {
        this.activeServiceDropdownIndex += increment;
        this.activeLocationDropdownIndex = this.noSuggestionsSelected;
        this.activeKeywordDropdownIndex = this.noSuggestionsSelected;
      } else if (this.activeServiceDropdownIndex + increment === this.serviceSliceIndex && this.servicesSeeMore) {
        this.activeServiceDropdownIndex += increment;
        this.seeLessMoreClicked = true;
        this.servicesSeeMore.nativeElement.focus();
      } else {
        this.activeLocationDropdownIndex = this.noSuggestionsSelected;
        this.activeServiceDropdownIndex = this.noSuggestionsSelected;
        this.activeKeywordDropdownIndex = 0;
      }
    } else if (this.activeKeywordDropdownIndex > this.noSuggestionsSelected) {
      if (this.activeKeywordDropdownIndex + increment === -1) {
        if (this.servicesSeeMore) {
          this.seeLessMoreClicked = true;
          this.servicesSeeMore.nativeElement.focus();
          this.activeServiceDropdownIndex = this.serviceSliceIndex;
        } else {
          this.activeServiceDropdownIndex = this.serviceSliceIndex - 1;
        }
        this.activeLocationDropdownIndex = this.noSuggestionsSelected;
        this.activeKeywordDropdownIndex = this.noSuggestionsSelected;
      } else if (this.activeKeywordDropdownIndex + increment < this.keywordSliceIndex) {
        this.activeServiceDropdownIndex += this.noSuggestionsSelected;
        this.activeLocationDropdownIndex = this.noSuggestionsSelected;
        this.activeKeywordDropdownIndex += increment;
      } else if (this.activeKeywordDropdownIndex + increment === this.keywordSliceIndex && this.keywordsSeeMore) {
        this.activeKeywordDropdownIndex += increment;
        this.seeLessMoreClicked = true;
        this.keywordsSeeMore.nativeElement.focus();
      } else {
        this.activeLocationDropdownIndex = this.noSuggestionsSelected;
        this.activeServiceDropdownIndex = this.noSuggestionsSelected;
        if (this.keywordsSeeMore) {
          this.activeKeywordDropdownIndex = this.keywordSliceIndex;
          this.seeLessMoreClicked = true;
          this.keywordsSeeMore.nativeElement.focus();
        } else {
          this.activeKeywordDropdownIndex = this.keywordSliceIndex - 1;
        }
      }
    }
  }

  expandLessClick(type: string): void {
    this.seeLessMoreFocused = false;
    this.isFromExpandLessClick = true;

    if (type === 'location') {
      this.locationSliceIndex =
        this.locationSliceIndex < this.suggestions?.locations?.length ? this.suggestions.locations.length : this.defaultSuggestionCount;
      this.activeLocationDropdownIndex = this.defaultSliceIndex;
    }
    if (type === 'services') {
      this.serviceSliceIndex =
        this.serviceSliceIndex < this.suggestions?.services?.length ? this.suggestions.services.length : this.defaultSuggestionCount;
      this.activeServiceDropdownIndex = this.defaultSliceIndex;
    }
    if (type === 'keywords') {
      this.keywordSliceIndex =
        this.keywordSliceIndex < this.suggestions?.keywords?.length ? this.suggestions.keywords.length : this.defaultSuggestionCount;
      this.activeKeywordDropdownIndex = this.defaultSliceIndex;
    }

    setTimeout(() => {
      this.searchElement.nativeElement.focus();
    }, 0);
  }

  seeLessMoreHover(type: string): void {
    if (this.isFromExpandLessClick) {
      this.isFromExpandLessClick = false;
      return;
    }
    this.seeLessMoreClicked = true;
    this.seeLessMoreFocused = true;

    setTimeout(() => {
      this.lastFocusWasFromKeyboard = true;
    }, 1000);

    if (type === 'location') {
      this.activeLocationDropdownIndex = this.locationSliceIndex;
      (this.locationSeeMore.nativeElement as HTMLElement).focus();
    }
    if (type === 'services') {
      this.activeServiceDropdownIndex = this.serviceSliceIndex;
      (this.servicesSeeMore.nativeElement as HTMLElement).focus();
    }
    if (type === 'keywords') {
      this.activeKeywordDropdownIndex = this.keywordSliceIndex;
      (this.keywordsSeeMore.nativeElement as HTMLElement).focus();
    }
  }

  hasSuggestions(): boolean {
    return this.suggestions.keywords.length > 0 || this.suggestions.services.length > 0 || this.suggestions.locations.length > 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.hideKeywordSuggestions?.previousValue != changes?.hideKeywordSuggestions?.currentValue) {
      this.setAutocompleteActive(false);
    }
  }

  onFocusGained(): void {
    this.inputInFocus = true;
    this.closeLocationSuggestion.emit();
  }

  onFocusLost(event: FocusEvent | any): void {
    if (
      (event.target && !this.locationSuggestionsContainer.nativeElement.contains(event.target)) ||
      (event.relatedTarget && !this.locationSuggestionsContainer.nativeElement.contains(event.relatedTarget))
    ) {
      this.inputInFocus = false;
    }
  }

  showError(): boolean {
    return !this.autocompleteActive && this.searchTerm !== '' && this.hasSuggestions() && !this.searchSelected;
  }
}
